// Shared 360 video recording system (extracted from NewVideo.js) so it can be
// reused by Video.js, StencilVideo.js and IntegratedVideo.js.
//
// Usage:
//   startCarVideoRecording({ config, clientId, videoEl, inspectionId, token, isIos })
//     -> call when the camera starts (after videoEl.srcObject is set)
//   stopCarVideoRecordingAndUpload()  -> call when the camera stops normally (uploads)
//   cancelCarVideoRecording()         -> call when leaving/aborting (no upload)
//
// Both platforms record via a dedicated off-screen canvas (separate stream, so
// the live camera preview is never disturbed). iOS: 720p, MP4/H.264. Android:
// 480p, WebM/VP8 (Android's MP4 MediaRecorder yields 0 chunks from a canvas).
// Only active when config.recordVideo === true. Auto stops + uploads at 50 MB.

import {
  reserveVideoUpload,
  releaseReservedVideo,
  uploadReservedVideo,
  patLogs,
} from "../context/utils";

let mediaRecorder = null;
let recordedChunks = [];
let recordingCanvas = null;
let recordingStream = null;
let recordingRafId = null;
let recordingDrawTimer = null;
let recordingDrawActive = false;
let recordedMimeType = "video/webm";
let recordingDataCheckTimer = null;
let receivedRecordingData = false;
let recordingBitrateValue = 800000;
let recordedBytes = 0;
let maxRecordBytes = 50 * 1024 * 1024; // auto stop + upload at 50 MB
let uploadWatchdog = null;
let ctx = {}; // { config, clientId, videoEl, inspectionId, token, isIos }

// Browser-console logging (visible in DevTools) so recording issues can be
// diagnosed live during a device test.
const clog = (...args) => {
  try {
    console.log("[carVideoRecorder]", ...args);
  } catch (e) {}
};

const isVideoRecordingEnabled = () =>
  ctx.config &&
  Object.keys(ctx.config).includes("recordVideo") &&
  (ctx.config["recordVideo"] === true || ctx.config["recordVideo"] === "true");

const getSupportedVideoMime = (isIos) => {
  // iOS records MP4 fine from a canvas. Android Chrome's MP4 MediaRecorder
  // produces 0 chunks from a canvas.captureStream (works only from the camera
  // stream), so on Android we prefer WebM/VP8, which is reliable with canvas.
  const candidates = isIos
    ? [
        "video/mp4;codecs=avc1.42E01E",
        "video/mp4;codecs=avc1",
        "video/mp4;codecs=h264",
        "video/mp4",
        "video/webm;codecs=vp8",
        "video/webm",
      ]
    : [
        "video/webm;codecs=vp8",
        "video/webm;codecs=vp9",
        "video/webm",
        "video/mp4;codecs=avc1.42E01E",
        "video/mp4",
      ];
  for (let i = 0; i < candidates.length; i++) {
    if (
      typeof MediaRecorder !== "undefined" &&
      typeof MediaRecorder.isTypeSupported === "function" &&
      MediaRecorder.isTypeSupported(candidates[i])
    ) {
      return candidates[i];
    }
  }
  return "";
};

const stopRecordingDrawLoop = () => {
  recordingDrawActive = false;
  if (recordingRafId) {
    cancelAnimationFrame(recordingRafId);
    recordingRafId = null;
  }
  if (recordingDrawTimer) {
    clearInterval(recordingDrawTimer);
    recordingDrawTimer = null;
  }
};

const clearUploadWatchdog = () => {
  if (uploadWatchdog) {
    clearTimeout(uploadWatchdog);
    uploadWatchdog = null;
  }
};

// Stop + upload when the recording reaches the size cap (the inspection keeps
// running; recording just stops growing so the file never exceeds the cap).
const trackRecordedSize = (size) => {
  recordedBytes += size;
  if (recordedBytes >= maxRecordBytes) {
    patLogs(
      `recording hit ${(recordedBytes / 1048576).toFixed(
        1,
      )}MB cap; stopping and uploading`,
      ctx.inspectionId,
    );
    stopCarVideoRecordingAndUpload();
  }
};

// iOS-safe fallback / Android path: record a CLONE of the camera track directly
// (encoding off the main thread -> smooth; separate track -> no preview lag).
const switchToDirectStreamRecording = (recordBitrate) => {
  try {
    stopRecordingDrawLoop();
    if (mediaRecorder) {
      mediaRecorder.ondataavailable = null;
      mediaRecorder.onstop = null;
      try {
        if (mediaRecorder.state !== "inactive") mediaRecorder.stop();
      } catch (e) {}
    }
    if (recordingStream) {
      recordingStream.getTracks().forEach((t) => t.stop());
      recordingStream = null;
    }
    recordingCanvas = null;

    if (!window.stream) return;
    const srcTrack = window.stream.getVideoTracks
      ? window.stream.getVideoTracks()[0]
      : null;
    if (!srcTrack) return;
    recordingStream = new MediaStream([srcTrack.clone()]);
    recordedChunks = [];
    recordedBytes = 0;
    receivedRecordingData = false;
    const mimeType = getSupportedVideoMime(ctx.isIos);
    const options = {
      videoBitsPerSecond: recordBitrate || recordingBitrateValue,
    };
    if (mimeType) options.mimeType = mimeType;
    mediaRecorder = new MediaRecorder(recordingStream, options);
    recordedMimeType =
      mediaRecorder.mimeType && mediaRecorder.mimeType.length > 0
        ? mediaRecorder.mimeType
        : mimeType || "video/mp4";
    mediaRecorder.ondataavailable = (e) => {
      if (e.data && e.data.size > 0) {
        receivedRecordingData = true;
        recordedChunks.push(e.data);
        clog("direct chunk", e.data.size, "bytes; total", recordedBytes);
        trackRecordedSize(e.data.size);
      }
    };
    mediaRecorder.onstop = () => {
      uploadRecordedVideoBlob();
    };
    mediaRecorder.start(1000);
    clog("direct-stream recording started", { mimeType: recordedMimeType });
    patLogs(
      "recording live camera stream directly (smooth, off-main-thread)",
      ctx.inspectionId,
    );
  } catch (err) {
    clog("direct stream recording FAILED", err && err.message);
    patLogs(`direct stream recording failed: ${err.message}`, ctx.inspectionId);
  }
};

export const startCarVideoRecording = (opts) => {
  try {
    ctx = opts || {};
    clog("startCarVideoRecording called", {
      recordVideo: ctx.config && ctx.config["recordVideo"],
      isIos: ctx.isIos,
      hasVideoEl: !!ctx.videoEl,
      inspectionId: ctx.inspectionId,
    });
    patLogs(
      `video recording: recordVideo flag = ${JSON.stringify(
        ctx.config && ctx.config["recordVideo"],
      )}`,
      ctx.inspectionId,
    );
    if (!isVideoRecordingEnabled()) {
      clog("SKIPPED: recordVideo not true in loaded config");
      patLogs(
        "video recording skipped: recordVideo not true in loaded config",
        ctx.inspectionId,
      );
      return;
    }
    if (typeof MediaRecorder === "undefined") {
      clog("SKIPPED: MediaRecorder not supported");
      patLogs(
        "video recording skipped: MediaRecorder not supported",
        ctx.inspectionId,
      );
      return;
    }
    const vw = ctx.videoEl;
    if (!vw) {
      clog("SKIPPED: no video element");
      patLogs("video recording skipped: no video element", ctx.inspectionId);
      return;
    }
    const config = ctx.config;

    // Per-platform defaults (config can still override any of these):
    // - iOS: 720p, native fps, MP4/H.264, 0.4 Mbps.
    // - Android: 720p, native fps, WebM/VP8, 0.8 Mbps.
    // Native fps = the camera track's real frame rate (derived below).
    const recordHeight = Object.keys(config["video-page"]).includes(
      "recordingHeight",
    )
      ? config["video-page"]["recordingHeight"]
      : 720;
    // null => native frame rate (derived from the camera track below).
    const recordFps = Object.keys(config["video-page"]).includes("recordingFps")
      ? config["video-page"]["recordingFps"]
      : null;
    const recordBitrate = Object.keys(config["video-page"]).includes(
      "recordingBitrate",
    )
      ? config["video-page"]["recordingBitrate"]
      : ctx.isIos
        ? 400000
        : 800000;
    recordingBitrateValue = recordBitrate;
    receivedRecordingData = false;
    recordedBytes = 0;
    maxRecordBytes =
      (Object.keys(config["video-page"]).includes("recordingMaxSizeMB")
        ? config["video-page"]["recordingMaxSizeMB"]
        : 50) *
      1024 *
      1024;

    // Both platforms record via a dedicated off-screen canvas so the recorder
    // runs on a SEPARATE canvas stream, never the camera track -> the live
    // camera preview is never disturbed (no backpressure/flicker). The recorded
    // video may judder under heavy ML (accepted); the preview stays smooth.
    recordingCanvas = document.createElement("canvas");
    recordingCanvas.width = Math.round((recordHeight * 16) / 9);
    recordingCanvas.height = recordHeight;
    const rctx = recordingCanvas.getContext("2d");

    const drawRecordingFrame = () => {
      try {
        if (!vw.videoWidth || !vw.videoHeight) return;
        const targetH = recordHeight;
        const targetW = Math.round(targetH * (vw.videoWidth / vw.videoHeight));
        if (recordingCanvas.width !== targetW) recordingCanvas.width = targetW;
        if (recordingCanvas.height !== targetH)
          recordingCanvas.height = targetH;
        rctx.drawImage(vw, 0, 0, targetW, targetH);
      } catch (e) {}
    };

    // Draw with a plain setInterval so the canvas is updated even when the
    // video doesn't fire requestVideoFrameCallback (that was producing 0 frames
    // on some Android devices). Draw once immediately so captureStream has a
    // painted canvas before recording starts.
    recordingDrawActive = true;
    // Native fps: read the camera track's real frame rate (fallback 30).
    let nativeFps = 30;
    try {
      const camTrack =
        window.stream && window.stream.getVideoTracks
          ? window.stream.getVideoTracks()[0]
          : null;
      const s = camTrack && camTrack.getSettings ? camTrack.getSettings() : null;
      if (s && s.frameRate) nativeFps = Math.max(1, Math.round(s.frameRate));
    } catch (e) {}
    const drawFps = recordFps || nativeFps;
    clog("draw fps", drawFps, "(native ~", nativeFps, ")");
    let drewOnce = false;
    let loggedNoDim = false;
    const drawTick = () => {
      if (!recordingDrawActive) return;
      if (!vw.videoWidth || !vw.videoHeight) {
        if (!loggedNoDim) {
          loggedNoDim = true;
          clog("draw waiting: video has no dimensions yet (videoWidth=0)");
        }
        return;
      }
      drawRecordingFrame();
      if (!drewOnce) {
        drewOnce = true;
        clog("first canvas draw ok", {
          videoWidth: vw.videoWidth,
          videoHeight: vw.videoHeight,
        });
      }
    };
    drawTick();
    recordingDrawTimer = setInterval(drawTick, Math.round(1000 / drawFps));

    recordingStream = recordingCanvas.captureStream(drawFps);
    const mimeType = getSupportedVideoMime(ctx.isIos);
    const options = { videoBitsPerSecond: recordBitrate };
    if (mimeType) options.mimeType = mimeType;
    recordedChunks = [];
    mediaRecorder = new MediaRecorder(recordingStream, options);
    recordedMimeType =
      mediaRecorder.mimeType && mediaRecorder.mimeType.length > 0
        ? mediaRecorder.mimeType
        : mimeType || "video/webm";
    mediaRecorder.ondataavailable = (e) => {
      if (e.data && e.data.size > 0) {
        receivedRecordingData = true;
        if (recordingDataCheckTimer) {
          clearTimeout(recordingDataCheckTimer);
          recordingDataCheckTimer = null;
        }
        recordedChunks.push(e.data);
        clog("canvas chunk", e.data.size, "bytes; total", recordedBytes);
        trackRecordedSize(e.data.size);
      }
    };
    mediaRecorder.onstop = () => {
      uploadRecordedVideoBlob();
    };
    mediaRecorder.start(1000);
    clog("canvas recording started", {
      recordHeight,
      recordFps,
      recordBitrate,
      mimeType: recordedMimeType,
    });
    patLogs(`${recordHeight}p video recording started`, ctx.inspectionId);

    // iOS only: older Safari canvas capture can yield no data -> fall back to
    // direct stream. Never do this on Android: the direct/clone path disturbs
    // the camera preview (flicker), so we keep Android strictly on the canvas.
    if (ctx.isIos) {
      if (recordingDataCheckTimer) clearTimeout(recordingDataCheckTimer);
      recordingDataCheckTimer = setTimeout(() => {
        recordingDataCheckTimer = null;
        if (!receivedRecordingData) {
          clog("iOS canvas produced no data in 2.5s -> falling back to direct");
          switchToDirectStreamRecording(recordBitrate);
        }
      }, 2500);
    }
  } catch (err) {
    clog("startCarVideoRecording FAILED", err && err.message);
    patLogs(`video recording start failed: ${err.message}`, ctx.inspectionId);
  }
};

// Stop recording and queue the upload. Reserves the upload slot synchronously
// so the success page accounts for the video before navigation happens.
export const stopCarVideoRecordingAndUpload = () => {
  try {
    if (recordingDataCheckTimer) {
      clearTimeout(recordingDataCheckTimer);
      recordingDataCheckTimer = null;
    }
    stopRecordingDrawLoop();
    clog(
      "stopCarVideoRecordingAndUpload called; recorder state =",
      mediaRecorder ? mediaRecorder.state : "none",
    );
    patLogs(
      `video recording stop requested; recorder state = ${
        mediaRecorder ? mediaRecorder.state : "none"
      }`,
      ctx.inspectionId,
    );
    if (mediaRecorder && mediaRecorder.state !== "inactive") {
      reserveVideoUpload();
      // Safety net: if onstop somehow never fires, force-release the reserved
      // slot so the success page is never blocked waiting on the video.
      clearUploadWatchdog();
      uploadWatchdog = setTimeout(() => {
        uploadWatchdog = null;
        clog("upload watchdog: onstop never fired; releasing slot");
        releaseReservedVideo();
      }, 20000);
      mediaRecorder.stop();
    }
  } catch (err) {
    patLogs(`video recording stop failed: ${err.message}`, ctx.inspectionId);
  }
};

const uploadRecordedVideoBlob = async () => {
  // onstop fired -> the upload flow (or no-chunks release) will run, and the XHR
  // has its own timeout, so the watchdog is no longer needed.
  clearUploadWatchdog();
  try {
    if (recordingStream) {
      recordingStream.getTracks().forEach((t) => t.stop());
    }
  } catch (e) {}
  clog("uploadRecordedVideoBlob: chunks =", recordedChunks.length);
  let formData;
  try {
    if (!recordedChunks.length) {
      clog("UPLOAD SKIPPED: no chunks were recorded");
      patLogs(
        "video upload skipped: no chunks were recorded",
        ctx.inspectionId,
      );
      releaseReservedVideo();
      return;
    }
    const ext = recordedMimeType.includes("mp4") ? "mp4" : "webm";
    const blob = new Blob(recordedChunks, { type: recordedMimeType });
    formData = new FormData();
    formData.append("video", blob, `${ctx.inspectionId}_video.${ext}`);
    formData.append("token", ctx.token);
    formData.append("inspectionId", ctx.inspectionId);
    patLogs(
      `uploading recorded video (${ext}) size ${(blob.size / 1024).toFixed(
        1,
      )}KB`,
      ctx.inspectionId,
    );
  } catch (err) {
    patLogs(`recorded video prepare failed: ${err.message}`, ctx.inspectionId);
    releaseReservedVideo();
    return;
  }
  clog("POSTing video to /uploadvideo ...");
  const ok = await uploadReservedVideo(formData);
  clog("upload result:", ok ? "SUCCESS" : "FAILED (backend/network)");
  patLogs(
    `video upload ${ok ? "succeeded" : "failed (backend/network)"}`,
    ctx.inspectionId,
  );
};

// Discard recording without uploading (used when leaving/aborting the module).
export const cancelCarVideoRecording = () => {
  clearUploadWatchdog();
  try {
    if (recordingDataCheckTimer) {
      clearTimeout(recordingDataCheckTimer);
      recordingDataCheckTimer = null;
    }
    stopRecordingDrawLoop();
    if (mediaRecorder && mediaRecorder.state !== "inactive") {
      mediaRecorder.ondataavailable = null;
      mediaRecorder.onstop = null;
      mediaRecorder.stop();
    }
    if (recordingStream) {
      recordingStream.getTracks().forEach((t) => t.stop());
    }
  } catch (e) {}
  mediaRecorder = null;
  recordedChunks = [];
  recordingStream = null;
  recordingCanvas = null;
};
